/* src/main.jsx — Production entry for the Xlooop prototype.
 *
 * Loads React + ReactDOM, exposes them globally (the prototype's .jsx files
 * read `const { useState } = React;` at module top-level), then imports
 * a SINGLE virtual module that concatenates all 16 prototype .jsx files in
 * dependency order. That keeps the prototype's shared-script-scope semantics
 * intact under ESM — cross-file `const X = ...` references resolve naturally
 * because all files now share one module scope.
 *
 * See vite.config.ts → prototypeBundle() plugin for the bundling logic.
 */
import React from 'react';
import * as ReactDOMNamespace from 'react-dom';
import { createRoot, hydrateRoot } from 'react-dom/client';
import './styles.css';

// Module namespace objects are FROZEN; build a fresh object that merges
// legacy react-dom exports + react-dom/client createRoot / hydrateRoot.
window.React = React;
window.ReactDOM = Object.assign({}, ReactDOMNamespace, {
  createRoot,
  hydrateRoot,
});

// Import the merged prototype bundle. The plugin concatenates 16 .jsx files;
// they share one module scope, preserving the prototype's design exactly.
import('virtual:prototype-bundle.jsx').catch((err) => {
  console.error('[x-web] prototype boot failed:', err);
  const root = document.getElementById('root');
  if (root && !root.hasChildNodes()) {
    const detail =
      err && err.message ? err.message : 'unknown error';
    // Pre-React fatal-error fallback. Can't import mailto helpers here
    // (loads before React/bundle scope), so inline a useful template.
    const fatalSubject = encodeURIComponent('Xlooop boot failed · fatal error');
    const fatalBody = encodeURIComponent(
      'Hi Xlooop team,\n\nThe Xlooop site failed to boot in my browser. Here is what I can share:\n\n' +
      '• Page I was on: ' + (location && location.href ? location.href : '[unknown]') + '\n' +
      '• Browser + OS: [e.g. Safari 17 on iOS 18]\n' +
      '• What I did just before the error: [e.g. typed a URL, clicked a link]\n' +
      '• Error detail (shown below): ' + detail.slice(0, 200) + '\n\n' +
      'Thanks,\n[your name]'
    );
    const fatalHref = 'mailto:hello@xlooop.com?subject=' + fatalSubject + '&body=' + fatalBody;
    root.innerHTML =
      '<div style="padding:48px 24px;text-align:center;font-family:system-ui,sans-serif;max-width:760px;margin:0 auto;color:#0F0F12">' +
      '<h1 style="font-size:28px;margin-bottom:16px">Xlooop boot failed</h1>' +
      '<p style="color:#555;line-height:1.6;margin-bottom:24px">' +
      'Email <a href="' + fatalHref + '" style="color:#6B4FFF">hello@xlooop.com</a> with what you saw (the link opens a pre-filled report).</p>' +
      '<pre style="text-align:left;background:#F7F7F9;padding:16px;border-radius:8px;font-family:ui-monospace,monospace;font-size:12px;line-height:1.5;overflow:auto;white-space:pre-wrap;word-break:break-word;color:#B91C1C">' +
      detail.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])) +
      '</pre></div>';
  }
});
